import { ChangeEvent, useMemo, useState } from "react";
import { Backdrop, Button, CircularProgress, TextField, Typography } from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import {
  findRelatorioGerencialBloco1,
  findRelatorioGerencialBloco2,
  findRelatorioGerencialBloco3,
  findRelatorioGerencialBloco4,
} from "API/apiRelatoriosTurma";
import { useAppFeedback } from "hooks/useAppFeedback";
import stylesButton from "styles/Button.module.css";
import stylesInput from "styles/Input.module.css";
import { addGovernmentReportLogo, GOVERNMENT_REPORT_HEADER_TEXT } from "utils/reportExportHeader";

import styles from "../Relatorios.module.css";

type RelatorioGerencialCensoSuasProps = {
  selectedReportLabel: string;
};

type Bloco1Data = {
  ano: number;
  indicadores_gerais: {
    total_acoes_ofertadas: number;
    total_turmas_ofertadas: number;
    total_aprovados: number;
    total_reprovados: number;
  };
  turmas_por_modalidade: Array<{
    modalidade_id: number;
    modalidade_nome: string;
    total_turmas: number;
  }>;
  desempenho_por_tipologia: Array<{
    tipologia_id: number;
    tipologia_nome: string;
    percurso_formativo?: string | null;
    total_turmas: number;
    total_aprovados: number;
    total_reprovados: number;
  }>;
};

type Bloco2Data = {
  ano: number;
  totais_gerais: {
    total_acoes: number;
    total_turmas: number;
    total_aprovados: number;
    total_reprovados: number;
  };
  acoes: Array<{
    acao_id: number;
    acao_nome: string;
    percurso_formativo?: string | null;
    data_inicio: string;
    data_fim: string;
    total_turmas: number;
    total_aprovados: number;
    total_reprovados: number;
    tipologias: Array<{
      tipologia_id: number;
      tipologia_nome: string;
      total_turmas: number;
      total_aprovados: number;
      total_reprovados: number;
      turmas: Array<{
        turma_id: number;
        turma_nome: string;
        data_inicio: string;
        data_fim: string;
        modalidade_id: number;
        modalidade_nome: string;
        total_aprovados: number;
        total_reprovados: number;
      }>;
    }>;
  }>;
};

type Bloco3Data = {
  ano: number;
  totais_gerais: {
    total_participacoes: number;
    total_cpfs_unicos: number;
  };
  publicos: Array<{
    publico_key: string;
    publico_alvo: string;
    total_participacoes: number;
    total_cpfs_unicos: number;
    modalidades: Array<{
      modalidade_id: number;
      modalidade_nome: string;
      total_participacoes: number;
      total_cpfs_unicos: number;
    }>;
  }>;
};

type Bloco4Data = {
  ano: number;
  totais_gerais: {
    total_participacoes: number;
    total_cpfs_unicos: number;
  };
  modalidades: Array<{
    modalidade_id: number;
    modalidade_nome: string;
  }>;
  municipios: Array<{
    municipio_id: number;
    municipio_nome: string;
    total_participacoes: number;
    total_cpfs_unicos: number;
    modalidades: Array<{
      modalidade_id: number;
      modalidade_nome: string;
      total_participacoes: number;
      total_cpfs_unicos: number;
      total_participacoes_municipal: number;
      total_cpfs_unicos_municipal: number;
      total_participacoes_estadual: number;
      total_cpfs_unicos_estadual: number;
    }>;
  }>;
};

const COLORS = {
  white: "FFFFFFFF",
  text: "FF111827",
  muted: "FF475569",
  border: "FFD1D5DB",
  borderStrong: "FF94A3B8",
  blueDark: "FF173B74",
  blue: "FF2563EB",
  blueSoft: "FFEAF2FF",
  blueCard: "FF4F83E1",
  blueCardDark: "FF2D5CB2",
  green: "FF0F8A4B",
  greenSoft: "FFEAF8EF",
  red: "FFB91C1C",
  redSoft: "FFFDECEC",
  orange: "FFEA7A14",
  orangeSoft: "FFFFF1E6",
  graySoft: "FFF8FAFC",
  pinkSoft: "FFFFE4E6",
  cpfBlue: "FFDDF0FF",
};

const NUMBER_FORMAT = "#,##0";

const setCellBorder = (
  cell: ExcelJS.Cell,
  color = COLORS.border,
  style: ExcelJS.BorderStyle = "thin",
) => {
  cell.border = {
    top: { style, color: { argb: color } },
    left: { style, color: { argb: color } },
    bottom: { style, color: { argb: color } },
    right: { style, color: { argb: color } },
  };
};

const styleRange = (
  worksheet: ExcelJS.Worksheet,
  startRow: number,
  startCol: number,
  endRow: number,
  endCol: number,
  options: {
    fill?: string;
    font?: Partial<ExcelJS.Font>;
    alignment?: Partial<ExcelJS.Alignment>;
    borderColor?: string;
    borderStyle?: ExcelJS.BorderStyle;
    numFmt?: string;
  } = {},
) => {
  for (let row = startRow; row <= endRow; row += 1) {
    for (let col = startCol; col <= endCol; col += 1) {
      const cell = worksheet.getCell(row, col);
      if (options.fill) {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: options.fill },
        };
      }
      if (options.font) {
        cell.font = {
          ...(cell.font || {}),
          ...options.font,
        };
      }
      if (options.alignment) {
        cell.alignment = {
          vertical: "middle",
          ...(cell.alignment || {}),
          ...options.alignment,
        };
      }
      if (options.numFmt) {
        cell.numFmt = options.numFmt;
      }
      setCellBorder(cell, options.borderColor, options.borderStyle);
    }
  }
};

const mergeAndStyle = (
  worksheet: ExcelJS.Worksheet,
  startRow: number,
  startCol: number,
  endRow: number,
  endCol: number,
  value: ExcelJS.CellValue,
  options: {
    fill?: string;
    font?: Partial<ExcelJS.Font>;
    alignment?: Partial<ExcelJS.Alignment>;
    borderColor?: string;
    borderStyle?: ExcelJS.BorderStyle;
    numFmt?: string;
  } = {},
) => {
  worksheet.mergeCells(startRow, startCol, endRow, endCol);
  worksheet.getCell(startRow, startCol).value = value;
  styleRange(worksheet, startRow, startCol, endRow, endCol, options);
};

const configureWorksheetBase = (worksheet: ExcelJS.Worksheet) => {
  worksheet.views = [{ showGridLines: false, state: "frozen", ySplit: 4 }];
  worksheet.properties.defaultRowHeight = 22;
  worksheet.pageSetup = {
    fitToPage: true,
    fitToWidth: 1,
    orientation: "landscape",
    paperSize: 9,
    margins: {
      left: 0.3,
      right: 0.3,
      top: 0.4,
      bottom: 0.4,
      header: 0.2,
      footer: 0.2,
    },
  };
};

const createBandHeader = (
  worksheet: ExcelJS.Worksheet,
  bandLabel: string,
  title: string,
  year: number,
  bandColor: string,
  titleRange: [number, number, number, number],
  yearRange: [number, number, number, number],
) => {
  mergeAndStyle(worksheet, 1, 1, 1, 8, bandLabel, {
    fill: bandColor,
    font: { bold: true, color: { argb: COLORS.white }, size: 11 },
    alignment: { horizontal: "left" },
    borderColor: bandColor,
  });

  mergeAndStyle(worksheet, 2, 1, 2, worksheet.columnCount, GOVERNMENT_REPORT_HEADER_TEXT, {
    font: { bold: false, size: 10, color: { argb: COLORS.text } },
    alignment: { horizontal: "left", vertical: "middle", wrapText: true, indent: 7 },
    borderColor: COLORS.white,
  });
  worksheet.getRow(2).height = 46;

  mergeAndStyle(
    worksheet,
    titleRange[0],
    titleRange[1],
    titleRange[2],
    titleRange[3],
    title,
    {
      font: { bold: true, size: 17, color: { argb: COLORS.text } },
      alignment: { horizontal: "left" },
      borderColor: COLORS.white,
    },
  );

  mergeAndStyle(
    worksheet,
    yearRange[0],
    yearRange[1],
    yearRange[2],
    yearRange[3],
    `Ano de Referencia: ${year}`,
    {
      fill: COLORS.white,
      font: { bold: true, size: 12, color: { argb: COLORS.text } },
      alignment: { horizontal: "center" },
      borderColor: bandColor,
      borderStyle: "medium",
    },
  );
};

const applyTableHeader = (
  worksheet: ExcelJS.Worksheet,
  rowNumber: number,
  color: string,
  startCol = 1,
  endCol = worksheet.columnCount,
) => {
  styleRange(worksheet, rowNumber, startCol, rowNumber, endCol, {
    fill: color,
    font: { bold: true, color: { argb: COLORS.white }, size: 10 },
    alignment: { horizontal: "center", wrapText: true },
    borderColor: COLORS.borderStrong,
  });
  worksheet.getRow(rowNumber).height = 26;
};

const applyRowStyle = (
  row: ExcelJS.Row,
  options: {
    fill?: string;
    font?: Partial<ExcelJS.Font>;
    alignment?: Partial<ExcelJS.Alignment>;
    borderColor?: string;
    numFmtColumns?: number[];
  } = {},
) => {
  row.eachCell((cell, colNumber) => {
    if (options.fill) {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: options.fill },
      };
    }
    if (options.font) {
      cell.font = {
        ...(cell.font || {}),
        ...options.font,
      };
    }
    cell.alignment = {
      vertical: "middle",
      horizontal: colNumber === 1 || colNumber === 2 ? "left" : "center",
      wrapText: true,
      ...(options.alignment || {}),
    };
    if (options.numFmtColumns?.includes(colNumber)) {
      cell.numFmt = NUMBER_FORMAT;
    }
    setCellBorder(cell, options.borderColor);
  });
};

const addInfoBox = (
  worksheet: ExcelJS.Worksheet,
  range: [number, number, number, number],
  title: string,
  message: string,
  color: string,
) => {
  mergeAndStyle(worksheet, range[0], range[1], range[0], range[3], title, {
    fill: color,
    font: { bold: true, color: { argb: COLORS.white }, size: 10 },
    alignment: { horizontal: "left" },
    borderColor: color,
  });

  mergeAndStyle(worksheet, range[0] + 1, range[1], range[2], range[3], message, {
    fill: COLORS.graySoft,
    font: { size: 10, color: { argb: COLORS.text } },
    alignment: { horizontal: "left", vertical: "top", wrapText: true },
    borderColor: COLORS.borderStrong,
  });
};

const normalizeText = (value: string) =>
  value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const formatDateBr = (value?: string | null) => {
  if (!value) {
    return "";
  }

  const datePart = value.split("T")[0]?.split(" ")[0];
  if (!datePart) {
    return value;
  }

  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) {
    return value;
  }

  return `${day}/${month}/${year}`;
};

const formatPeriodBr = (start?: string | null, end?: string | null) => {
  const formattedStart = formatDateBr(start);
  const formattedEnd = formatDateBr(end);

  if (formattedStart && formattedEnd) {
    return `${formattedStart} a ${formattedEnd}`;
  }

  return formattedStart || formattedEnd || "-";
};

const isDistanceMode = (modalidade: string) => {
  const normalized = normalizeText(modalidade);
  return normalized.includes("ead") || normalized.includes("distancia") || normalized.includes("online");
};

const getBloco4AmbitoTotals = (
  modalidades: Bloco4Data["municipios"][number]["modalidades"],
  ambito: "municipal" | "estadual",
) =>
  modalidades.reduce(
    (totais, modalidade) => {
      const totalParticipacoes =
        ambito === "municipal"
          ? modalidade.total_participacoes_municipal || 0
          : modalidade.total_participacoes_estadual || 0;
      const totalCpfsUnicos =
        ambito === "municipal"
          ? modalidade.total_cpfs_unicos_municipal || 0
          : modalidade.total_cpfs_unicos_estadual || 0;

      if (isDistanceMode(modalidade.modalidade_nome)) {
        totais.participacoesDistancia += totalParticipacoes;
      } else {
        totais.participacoesPresenciais += totalParticipacoes;
      }

      totais.totalParticipacoes += totalParticipacoes;
      totais.totalCpfsUnicos += totalCpfsUnicos;

      return totais;
    },
    {
      participacoesPresenciais: 0,
      participacoesDistancia: 0,
      totalParticipacoes: 0,
      totalCpfsUnicos: 0,
    },
  );

const estimateCellTextLength = (value: ExcelJS.CellValue) => {
  if (value === null || value === undefined) {
    return 0;
  }

  if (typeof value === "object") {
    if ("richText" in value && Array.isArray(value.richText)) {
      return value.richText.map((item) => item.text || "").join("").length;
    }
    if ("text" in value && typeof value.text === "string") {
      return value.text.length;
    }
    if ("result" in value && value.result !== undefined) {
      return String(value.result).length;
    }
  }

  return String(value).length;
};

const autoFitWorksheet = (
  worksheet: ExcelJS.Worksheet,
  options: { minWidth?: number; maxWidth?: number } = {},
) => {
  const minWidth = options.minWidth ?? 10;
  const maxWidth = options.maxWidth ?? 42;

  worksheet.columns.forEach((column) => {
    const values = column.values ?? [];
    let maxLength = minWidth;

    values.forEach((value) => {
      const cellLength = estimateCellTextLength(value);
      if (cellLength > maxLength) {
        maxLength = cellLength;
      }
    });

    column.width = Math.min(Math.max(maxLength + 2, minWidth), maxWidth);
  });

  worksheet.eachRow((row) => {
    let largestCell = 0;

    row.eachCell({ includeEmpty: true }, (cell) => {
      const cellLength = estimateCellTextLength(cell.value);
      if (cellLength > largestCell) {
        largestCell = cellLength;
      }
    });

    row.height = Math.max(22, Math.min(64, Math.ceil(largestCell / 18) * 14));
  });
};

const buildBloco1Sheet = (workbook: ExcelJS.Workbook, data: Bloco1Data) => {
  const ws = workbook.addWorksheet("1. Visao Geral");
  configureWorksheetBase(ws);
  ws.columns = [
    { width: 4 },
    { width: 24 },
    { width: 16 },
    { width: 16 },
    { width: 4 },
    { width: 21 },
    { width: 16 },
    { width: 16 },
    { width: 24 },
    { width: 16 },
  ];

  createBandHeader(
    ws,
    "1. Visao Geral",
    "Indicadores Gerais",
    data.ano,
    COLORS.blueDark,
    [3, 1, 3, 5],
    [3, 6, 3, 8],
  );

  mergeAndStyle(ws, 5, 1, 5, 8, "Oferta de Capacitacoes e Desempenho Geral", {
    font: { bold: true, size: 13, color: { argb: COLORS.text } },
    alignment: { horizontal: "left" },
    borderColor: COLORS.white,
  });

  mergeAndStyle(ws, 7, 1, 10, 4, "1  Oferta de Capacitacoes", {
    fill: COLORS.blueCard,
    font: { bold: true, size: 12, color: { argb: COLORS.white } },
    alignment: { horizontal: "left", vertical: "top" },
    borderColor: COLORS.blueCardDark,
    borderStyle: "medium",
  });
  mergeAndStyle(ws, 7, 5, 10, 8, "2  Desempenho Geral de Alunos", {
    fill: COLORS.blueCard,
    font: { bold: true, size: 12, color: { argb: COLORS.white } },
    alignment: { horizontal: "left", vertical: "top" },
    borderColor: COLORS.blueCardDark,
    borderStyle: "medium",
  });

  mergeAndStyle(ws, 11, 1, 11, 3, "Total de Macroacoes Ofertadas", {
    fill: COLORS.graySoft,
    font: { bold: true },
    alignment: { horizontal: "left" },
  });
  ws.getCell("D11").value = data.indicadores_gerais.total_acoes_ofertadas;
  styleRange(ws, 11, 4, 11, 4, {
    fill: COLORS.graySoft,
    font: { bold: true, size: 12 },
    alignment: { horizontal: "center" },
    numFmt: NUMBER_FORMAT,
  });

  mergeAndStyle(ws, 12, 1, 12, 3, "Total de Turmas Ofertadas", {
    fill: COLORS.graySoft,
    font: { bold: true },
    alignment: { horizontal: "left" },
  });
  ws.getCell("D12").value = data.indicadores_gerais.total_turmas_ofertadas;
  styleRange(ws, 12, 4, 12, 4, {
    fill: COLORS.graySoft,
    font: { bold: true, size: 12 },
    alignment: { horizontal: "center" },
    numFmt: NUMBER_FORMAT,
  });

  mergeAndStyle(ws, 11, 5, 11, 7, "Total Geral de Aprovados", {
    fill: COLORS.greenSoft,
    font: { bold: true },
    alignment: { horizontal: "left" },
  });
  ws.getCell("H11").value = data.indicadores_gerais.total_aprovados;
  styleRange(ws, 11, 8, 11, 8, {
    fill: "FFC8F3D5",
    font: { bold: true, size: 12 },
    alignment: { horizontal: "center" },
    numFmt: NUMBER_FORMAT,
  });

  mergeAndStyle(ws, 12, 5, 12, 7, "Total Geral de Reprovados", {
    fill: COLORS.redSoft,
    font: { bold: true },
    alignment: { horizontal: "left" },
  });
  ws.getCell("H12").value = data.indicadores_gerais.total_reprovados;
  styleRange(ws, 12, 8, 12, 8, {
    fill: "FFFBCACA",
    font: { bold: true, size: 12 },
    alignment: { horizontal: "center" },
    numFmt: NUMBER_FORMAT,
  });

  mergeAndStyle(ws, 14, 1, 14, 8, "Turmas por Modalidade", {
    fill: COLORS.blueSoft,
    font: { bold: true, color: { argb: COLORS.text } },
    alignment: { horizontal: "center" },
    borderColor: COLORS.borderStrong,
  });

  const modalidades = [...data.turmas_por_modalidade];
  while (modalidades.length < 3) {
    modalidades.push({
      modalidade_id: 0,
      modalidade_nome: "-",
      total_turmas: 0,
    });
  }

  modalidades.slice(0, 3).forEach((item, index) => {
    const startCol = 1 + index * 2;
    mergeAndStyle(ws, 15, startCol, 15, startCol + 1, item.modalidade_nome, {
      fill: COLORS.white,
      font: { bold: true, size: 10 },
      alignment: { horizontal: "center" },
      borderColor: COLORS.borderStrong,
    });
    mergeAndStyle(ws, 16, startCol, 16, startCol + 1, item.total_turmas, {
      fill: COLORS.white,
      font: { bold: true, size: 12 },
      alignment: { horizontal: "center" },
      borderColor: COLORS.borderStrong,
      numFmt: NUMBER_FORMAT,
    });
  });

  mergeAndStyle(ws, 18, 1, 18, 10, "Desempenho por Tipologia da Acao", {
    font: { bold: true, size: 13, color: { argb: COLORS.text } },
    alignment: { horizontal: "left" },
    borderColor: COLORS.white,
  });

  const tipologiaHeader = ws.addRow([
    "Tipologia da Acao (Macroacao)",
    "",
    "Percurso Formativo",
    "",
    "Total de Turmas",
    "",
    "Participantes Aprovados",
    "",
    "Participantes Reprovados",
    "",
  ]);
  ws.mergeCells(tipologiaHeader.number, 1, tipologiaHeader.number, 2);
  ws.mergeCells(tipologiaHeader.number, 3, tipologiaHeader.number, 4);
  ws.mergeCells(tipologiaHeader.number, 5, tipologiaHeader.number, 6);
  ws.mergeCells(tipologiaHeader.number, 7, tipologiaHeader.number, 8);
  ws.mergeCells(tipologiaHeader.number, 9, tipologiaHeader.number, 10);
  applyTableHeader(ws, tipologiaHeader.number, COLORS.green, 1, 10);

  data.desempenho_por_tipologia.forEach((item, index) => {
    const row = ws.addRow([
      item.tipologia_nome,
      "",
      item.percurso_formativo || "-",
      "",
      item.total_turmas,
      "",
      item.total_aprovados,
      "",
      item.total_reprovados,
      "",
    ]);
    ws.mergeCells(row.number, 1, row.number, 2);
    ws.mergeCells(row.number, 3, row.number, 4);
    ws.mergeCells(row.number, 5, row.number, 6);
    ws.mergeCells(row.number, 7, row.number, 8);
    ws.mergeCells(row.number, 9, row.number, 10);
    applyRowStyle(row, {
      fill: index % 2 === 0 ? COLORS.white : "FFF7F7F7",
      numFmtColumns: [5, 7, 9],
    });
  });

  const totalRow = ws.addRow([
    "TOTAL GERAL",
    "",
    "",
    "",
    data.indicadores_gerais.total_turmas_ofertadas,
    "",
    data.indicadores_gerais.total_aprovados,
    "",
    data.indicadores_gerais.total_reprovados,
    "",
  ]);
  ws.mergeCells(totalRow.number, 1, totalRow.number, 2);
  ws.mergeCells(totalRow.number, 3, totalRow.number, 4);
  ws.mergeCells(totalRow.number, 5, totalRow.number, 6);
  ws.mergeCells(totalRow.number, 7, totalRow.number, 8);
  ws.mergeCells(totalRow.number, 9, totalRow.number, 10);
  applyRowStyle(totalRow, {
    fill: COLORS.white,
    font: { bold: true, size: 11 },
    numFmtColumns: [5, 7, 9],
    borderColor: COLORS.text,
  });

  autoFitWorksheet(ws, { minWidth: 12, maxWidth: 34 });
};

const buildBloco2Sheet = (workbook: ExcelJS.Workbook, data: Bloco2Data) => {
  const ws = workbook.addWorksheet("3. Detalhamento");
  configureWorksheetBase(ws);
  ws.columns = [
    { width: 28 },
    { width: 24 },
    { width: 22 },
    { width: 18 },
    { width: 14 },
    { width: 14 },
    { width: 20 },
    { width: 18 },
    { width: 24 },
  ];

  createBandHeader(
    ws,
    "3. Detalhamento Acao/Turma",
    "Detalhamento Analitico por Acao e Turma",
    data.ano,
    COLORS.orange,
    [3, 1, 3, 5],
    [3, 6, 3, 8],
  );

  addInfoBox(
    ws,
    [5, 7, 8, 8],
    "Conferencia de Dados",
    "Esta visualizacao analitica permite auditar e conferir a origem dos CPFs aprovados que compoem os totalizadores do Censo SUAS.",
    COLORS.orange,
  );

  const header = ws.addRow([
    "Macroacao",
    "Tipologia (Macro)",
    "Percurso Formativo",
    "Turma",
    "Modalidade da Turma",
    "Aprovados",
    "Reprovados",
    "Periodo da Acao",
    "Periodo da Turma",
  ]);
  applyTableHeader(ws, header.number, "FFC76A13");

  data.acoes.forEach((acao, acaoIndex) => {
    const acaoRow = ws.addRow([
      acao.acao_nome,
        "",
        acao.percurso_formativo || "-",
        "-",
        "-",
        acao.total_aprovados,
        acao.total_reprovados,
        formatPeriodBr(acao.data_inicio, acao.data_fim),
        `${acao.total_turmas} turma(s)`,
      ]);
    applyRowStyle(acaoRow, {
      fill: acaoIndex % 2 === 0 ? "FFFFE7D1" : "FFFFF1E6",
      font: { bold: true, size: 11 },
      numFmtColumns: [6, 7],
      borderColor: COLORS.borderStrong,
    });

    acao.tipologias.forEach((tipologia) => {
      const tipologiaRow = ws.addRow([
        "",
        tipologia.tipologia_nome,
        "-",
        "-",
        "-",
        tipologia.total_aprovados,
        tipologia.total_reprovados,
        "",
        `${tipologia.total_turmas} turma(s)`,
      ]);
      applyRowStyle(tipologiaRow, {
        fill: COLORS.orangeSoft,
        font: { bold: true },
        numFmtColumns: [6, 7],
      });

      tipologia.turmas.forEach((turma, turmaIndex) => {
        const row = ws.addRow([
          "",
          "",
          "",
          turma.turma_nome,
          turma.modalidade_nome,
          turma.total_aprovados,
          turma.total_reprovados,
          "",
          formatPeriodBr(turma.data_inicio, turma.data_fim),
        ]);
        applyRowStyle(row, {
          fill: turmaIndex % 2 === 0 ? COLORS.white : "FFFAFAFA",
          numFmtColumns: [6, 7],
        });
      });
    });
  });

  const totalRow = ws.addRow([
    "TOTAL GERAL",
    "",
    "",
    "",
    "",
    data.totais_gerais.total_aprovados,
    data.totais_gerais.total_reprovados,
    "",
    `${data.totais_gerais.total_turmas} turma(s)`,
  ]);
  applyRowStyle(totalRow, {
    fill: COLORS.white,
    font: { bold: true, size: 11 },
    numFmtColumns: [6, 7],
    borderColor: COLORS.text,
  });

  mergeAndStyle(ws, totalRow.number + 1, 1, totalRow.number + 1, 9, "* Nota: totais consideram a soma de turmas e participantes conforme o detalhamento analitico.", {
    font: { italic: true, size: 10, color: { argb: COLORS.text } },
    alignment: { horizontal: "left" },
    borderColor: COLORS.white,
  });

  autoFitWorksheet(ws, { minWidth: 12, maxWidth: 38 });
};

const buildBloco3Sheet = (workbook: ExcelJS.Workbook, data: Bloco3Data) => {
  const ws = workbook.addWorksheet("2. Censo SUAS");
  configureWorksheetBase(ws);
  ws.columns = [
    { width: 28 },
    { width: 22 },
    { width: 18 },
    { width: 22 },
  ];

  createBandHeader(
    ws,
    "2. Censo SUAS",
    "Censo SUAS - Publico Qualificado por Modalidade",
    data.ano,
    COLORS.blueDark,
    [3, 1, 3, 2],
    [3, 3, 3, 4],
  );

  const header = ws.addRow([
    "Publico-Alvo",
    "Modalidade (Turma)",
    "Participacoes (Soma Bruta)",
    "Pessoas Qualificadas (CPFs Unicos)",
  ]);
  applyTableHeader(ws, header.number, COLORS.green);
  ws.getCell(header.number, 4).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: COLORS.pinkSoft },
  };
  ws.getCell(header.number, 4).font = { bold: true, color: { argb: COLORS.text }, size: 10 };

  data.publicos.forEach((publico, publicoIndex) => {
    const startRow = ws.rowCount + 1;
    const modalidades = publico.modalidades.length > 0
      ? publico.modalidades
      : [{ modalidade_id: 0, modalidade_nome: "-", total_participacoes: 0, total_cpfs_unicos: 0 }];

    modalidades.forEach((modalidade, modalidadeIndex) => {
      const row = ws.addRow([
        publico.publico_alvo,
        modalidade.modalidade_nome,
        modalidade.total_participacoes,
        publico.total_cpfs_unicos,
      ]);
      applyRowStyle(row, {
        fill: (publicoIndex + modalidadeIndex) % 2 === 0 ? COLORS.white : "FFF4F4F5",
        numFmtColumns: [3, 4],
      });
      ws.getCell(row.number, 4).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: COLORS.pinkSoft },
      };
      ws.getCell(row.number, 4).font = { bold: true };
    });

    const endRow = ws.rowCount;
    if (endRow > startRow) {
      ws.mergeCells(startRow, 1, endRow, 1);
      ws.mergeCells(startRow, 4, endRow, 4);
    }
    ws.getCell(startRow, 1).alignment = { vertical: "middle", horizontal: "left", wrapText: true };
    ws.getCell(startRow, 4).alignment = { vertical: "middle", horizontal: "center", wrapText: true };
  });

  const totalRow = ws.addRow([
    "TOTAL",
    "",
    data.totais_gerais.total_participacoes,
    data.totais_gerais.total_cpfs_unicos,
  ]);
  applyRowStyle(totalRow, {
    fill: COLORS.white,
    font: { bold: true, size: 11 },
    numFmtColumns: [3, 4],
    borderColor: COLORS.text,
  });
  ws.mergeCells(totalRow.number, 1, totalRow.number, 2);

  mergeAndStyle(ws, totalRow.number + 1, 1, totalRow.number + 1, 4, "* Nota: Pessoas Qualificadas sao contadas apenas uma vez por CPF.", {
    font: { italic: true, size: 10, color: { argb: COLORS.text } },
    alignment: { horizontal: "left" },
    borderColor: COLORS.white,
  });

  autoFitWorksheet(ws, { minWidth: 10, maxWidth: 34 });
};

const buildBloco4Sheet = (workbook: ExcelJS.Workbook, data: Bloco4Data) => {
  const ws = workbook.addWorksheet("4. Alcance Territorial");
  configureWorksheetBase(ws);
  ws.columns = [
    { width: 24 },
    { width: 18 },
    { width: 18 },
    { width: 18 },
    { width: 22 },
    { width: 18 },
    { width: 18 },
    { width: 18 },
    { width: 22 },
  ];

  createBandHeader(
    ws,
    "4. Alcance Territorial",
    `Tabela de Alcance Territorial por Municipio - ${data.ano}`,
    data.ano,
    COLORS.red,
    [3, 1, 3, 5],
    [3, 6, 3, 9],
  );

  addInfoBox(
    ws,
    [5, 8, 8, 9],
    "Dica de Uso",
    "Os dados sao separados entre os ambitos municipal e estadual para facilitar a analise territorial.",
    COLORS.red,
  );

  const header = ws.addRow([
    "Municipio",
    "Participacoes Presenciais Municipais",
    "Participacoes a Distancia Municipais",
    "Total de Participacoes Municipais",
    "Pessoas Qualificadas Municipais (CPFs Unicos)",
    "Participacoes Presenciais Estaduais",
    "Participacoes a Distancia Estaduais",
    "Total de Participacoes Estaduais",
    "Pessoas Qualificadas Estaduais (CPFs Unicos)",
  ]);
  applyTableHeader(ws, header.number, "FFD62828");
  [5, 9].forEach((column) => {
    ws.getCell(header.number, column).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: COLORS.cpfBlue },
    };
    ws.getCell(header.number, column).font = { bold: true, color: { argb: COLORS.text }, size: 10 };
  });

  const totaisMunicipais = {
    participacoesPresenciais: 0,
    participacoesDistancia: 0,
    totalParticipacoes: 0,
    totalCpfsUnicos: 0,
  };
  const totaisEstaduais = {
    participacoesPresenciais: 0,
    participacoesDistancia: 0,
    totalParticipacoes: 0,
    totalCpfsUnicos: 0,
  };

  data.municipios.forEach((municipio, index) => {
    const municipal = getBloco4AmbitoTotals(municipio.modalidades, "municipal");
    const estadual = getBloco4AmbitoTotals(municipio.modalidades, "estadual");

    totaisMunicipais.participacoesPresenciais += municipal.participacoesPresenciais;
    totaisMunicipais.participacoesDistancia += municipal.participacoesDistancia;
    totaisMunicipais.totalParticipacoes += municipal.totalParticipacoes;
    totaisMunicipais.totalCpfsUnicos += municipal.totalCpfsUnicos;
    totaisEstaduais.participacoesPresenciais += estadual.participacoesPresenciais;
    totaisEstaduais.participacoesDistancia += estadual.participacoesDistancia;
    totaisEstaduais.totalParticipacoes += estadual.totalParticipacoes;
    totaisEstaduais.totalCpfsUnicos += estadual.totalCpfsUnicos;

    const row = ws.addRow([
      municipio.municipio_nome,
      municipal.participacoesPresenciais,
      municipal.participacoesDistancia,
      municipal.totalParticipacoes,
      municipal.totalCpfsUnicos,
      estadual.participacoesPresenciais,
      estadual.participacoesDistancia,
      estadual.totalParticipacoes,
      estadual.totalCpfsUnicos,
    ]);
    applyRowStyle(row, {
      fill: index % 2 === 0 ? COLORS.white : "FFF3F4F6",
      numFmtColumns: [2, 3, 4, 5, 6, 7, 8, 9],
    });
    [5, 9].forEach((column) => {
      ws.getCell(row.number, column).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: COLORS.cpfBlue },
      };
      ws.getCell(row.number, column).font = { bold: true };
    });
  });

  const totalRow = ws.addRow([
    "TOTAL ESTADO",
    totaisMunicipais.participacoesPresenciais,
    totaisMunicipais.participacoesDistancia,
    totaisMunicipais.totalParticipacoes,
    totaisMunicipais.totalCpfsUnicos,
    totaisEstaduais.participacoesPresenciais,
    totaisEstaduais.participacoesDistancia,
    totaisEstaduais.totalParticipacoes,
    totaisEstaduais.totalCpfsUnicos,
  ]);
  applyRowStyle(totalRow, {
    fill: COLORS.white,
    font: { bold: true, size: 11 },
    numFmtColumns: [2, 3, 4, 5, 6, 7, 8, 9],
    borderColor: COLORS.text,
  });

  autoFitWorksheet(ws, { minWidth: 12, maxWidth: 34 });
};

const RelatorioGerencialCensoSuas = ({ selectedReportLabel }: RelatorioGerencialCensoSuasProps) => {
  const { showError, showSuccess } = useAppFeedback();
  const [ano, setAno] = useState(String(new Date().getFullYear()));
  const [generatingReport, setGeneratingReport] = useState(false);

  const anoNumerico = useMemo(() => Number(ano), [ano]);

  const handleClear = () => {
    setAno(String(new Date().getFullYear()));
  };

  const handleGenerateReport = async () => {
    if (!ano || Number.isNaN(anoNumerico) || anoNumerico < 2000) {
      showError("Informe um ano de referencia valido.");
      return;
    }

    try {
      setGeneratingReport(true);

      const [bloco1, bloco2, bloco3, bloco4] = await Promise.all([
        findRelatorioGerencialBloco1<Bloco1Data>({ ano: anoNumerico, perPage: 999999 }),
        findRelatorioGerencialBloco2<Bloco2Data>({ ano: anoNumerico, perPage: 999999 }),
        findRelatorioGerencialBloco3<Bloco3Data>({ ano: anoNumerico, perPage: 999999 }),
        findRelatorioGerencialBloco4<Bloco4Data>({ ano: anoNumerico, perPage: 999999 }),
      ]);

      const workbook = new ExcelJS.Workbook();
      workbook.creator = "SISCAP";
      workbook.lastModifiedBy = "SISCAP";
      workbook.created = new Date();
      workbook.modified = new Date();

      buildBloco1Sheet(workbook, bloco1.data);
      buildBloco3Sheet(workbook, bloco3.data);
      buildBloco2Sheet(workbook, bloco2.data);
      buildBloco4Sheet(workbook, bloco4.data);

      await Promise.all(
        workbook.worksheets.map((worksheet) => addGovernmentReportLogo(workbook, worksheet, 1)),
      );

      const excelBuffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([excelBuffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `relatorio_gerencial_censo_suas_${anoNumerico}.xlsx`,
      );

      showSuccess("Relatorio gerado com sucesso.");
    } catch (error) {
      showError("Nao foi possivel gerar o relatorio gerencial.", error);
    } finally {
      setGeneratingReport(false);
    }
  };

  return (
    <>
      <div className={styles.reportFilterBody}>
        <div className={styles.reportHeaderRow}>
          <div className={styles.reportSelectedWrap}>
            <span className={styles.reportSelectedLabel}>Relatorio selecionado:</span>
            <span className={styles.reportSelectedChip}>{selectedReportLabel}</span>
          </div>

          <div className={styles.exportFormatCard}>
            <div className={styles.exportFormatLabel}>Formato de exportacao</div>
            <div className={styles.exportFormatValue}>
              <TableChartOutlinedIcon className={styles.exportFormatIcon} />
              XLSX
            </div>
          </div>
        </div>

        <div className={styles.reportDivider}></div>

        <div className={styles.filterSection}>
          <div className={styles.filterBlockTitle}>
            Ano de referencia <span className={styles.optionalText}>(obrigatorio)</span>
          </div>
          <div className={styles.filterGridTwo}>
            <div>
              <label className={stylesInput.titleInputSoft}>Ano</label>
              <TextField
                type="number"
                fullWidth
                size="small"
                variant="standard"
                value={ano}
                onChange={(event: ChangeEvent<HTMLInputElement>) => setAno(event.target.value)}
                inputProps={{ min: 2000, max: 2100 }}
                InputProps={{ className: stylesInput.inputSoft, disableUnderline: true }}
              />
            </div>
          </div>
        </div>

        <div className={styles.reportInfoBanner}>
          <InfoOutlinedIcon className={styles.reportInfoBannerIcon} />
          <span>
            Este relatorio gera uma unica planilha com quatro abas: visao geral, censo suas, detalhamento
            por acao/turma e alcance territorial.
          </span>
        </div>

        <div className={styles.selectionSummaryCard}>
          <div className={styles.selectionSummaryTitle}>Resumo da selecao</div>
          <div className={styles.selectionSummaryGrid}>
            <div className={styles.selectionSummaryItem}>
              <CalendarMonthOutlinedIcon className={styles.selectionSummaryIcon} />
              <div>
                <div className={styles.selectionSummaryLabel}>Ano de referencia</div>
                <div className={styles.selectionSummaryValue}>{ano || "-"}</div>
              </div>
            </div>
            <div className={styles.selectionSummaryItem}>
              <GridViewOutlinedIcon className={styles.selectionSummaryIcon} />
              <div>
                <div className={styles.selectionSummaryLabel}>Abas</div>
                <div className={styles.selectionSummaryValue}>4 blocos</div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.reportActionsRow}>
          <Button
            variant="outlined"
            className={stylesButton.buttonSoftMuted}
            startIcon={<DeleteOutlineOutlinedIcon />}
            onClick={handleClear}
          >
            Limpar filtros
          </Button>

          <div className={styles.reportActionsRight}>
            <div className={styles.generateButtonGroup}>
              <Button
                variant="contained"
                className={styles.generateButton}
                startIcon={<DownloadOutlinedIcon />}
                onClick={handleGenerateReport}
                disabled={generatingReport}
              >
                Gerar relatorio
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Backdrop
        open={generatingReport}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 100,
          flexDirection: "column",
          gap: 2,
          backgroundColor: "rgba(15, 23, 42, 0.45)",
        }}
      >
        <CircularProgress color="inherit" />
        <Typography sx={{ fontWeight: 600 }}>Gerando relatorio...</Typography>
      </Backdrop>
    </>
  );
};

export default RelatorioGerencialCensoSuas;
